/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, FormControl, InputLabel, Select, MenuItem, Skeleton,
  Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ScienceIcon from '@mui/icons-material/Science';
import BiotechIcon from '@mui/icons-material/Biotech';
import { useNavigate } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDDatePicker from 'components/MDDatePicker';
import { api } from '../../../../api';
import { DATE_RANGE_PRESETS, resolveDateRange } from '../../../../utils/dateRangePresets';
import {
  PROCEDURE_TYPE_LIST, PROCEDURE_TYPE_LABELS, PROCEDURE_TYPE_HEX,
  PROCEDURE_STATUS_LIST, PROCEDURE_STATUS_LABELS, PROCEDURE_STATUS_HEX,
} from '../../../ivfProcedures/constants';

const TOTAL_HEX = '#00798E'; // brand teal — same convention as AppointmentStats' Total tile

const EMPTY_BY_TYPE = PROCEDURE_TYPE_LIST.reduce((acc, t) => ({ ...acc, [t]: 0 }), {});
const EMPTY_BY_STATUS = PROCEDURE_STATUS_LIST.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
const EMPTY_MATRIX = PROCEDURE_TYPE_LIST.reduce((acc, t) => ({ ...acc, [t]: { ...EMPTY_BY_STATUS } }), {});

function StatTile({
  label, count, hex, Icon, loading, onClick, solid,
}) {
  return (
    <MDBox
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        borderRadius: 2,
        border: solid ? 'none' : '1px solid',
        borderColor: solid ? 'transparent' : alpha(hex, 0.22),
        bgcolor: solid ? hex : alpha(hex, 0.06),
        boxShadow: solid ? `0 4px 12px ${alpha(hex, 0.35)}` : 'none',
        px: 1.5,
        py: 1.25,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
        '&:hover': solid ? {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 18px ${alpha(hex, 0.45)}`,
        } : {
          bgcolor: alpha(hex, 0.12),
          borderColor: alpha(hex, 0.45),
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 14px rgba(0,0,0,0.08)',
        },
      }}
    >
      <MDBox display="flex" alignItems="center" gap={0.75} minWidth={0}>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={24}
          height={24}
          borderRadius="50%"
          flexShrink={0}
          sx={{ bgcolor: solid ? 'rgba(255,255,255,0.25)' : hex }}
        >
          <Icon sx={{ color: '#fff', fontSize: 14 }} />
        </MDBox>
        <MDTypography
          variant="caption"
          fontWeight="medium"
          sx={{
            color: solid ? 'rgba(255,255,255,0.9)' : '#7b809a',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            fontSize: '0.7rem',
            lineHeight: 1.2,
          }}
        >
          {label}
        </MDTypography>
      </MDBox>
      {loading ? (
        <Skeleton
          variant="text"
          width={36}
          height={30}
          sx={{ bgcolor: solid ? 'rgba(255,255,255,0.24)' : undefined }}
        />
      ) : (
        <MDTypography
          variant="h5"
          fontWeight="bold"
          sx={{ color: solid ? '#fff' : hex, lineHeight: 1.1 }}
        >
          {count}
        </MDTypography>
      )}
    </MDBox>
  );
}

function Cell({
  count, hex, loading, onClick, bold,
}) {
  return (
    <TableCell
      align="center"
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        py: 1,
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        '&:hover': onClick ? { bgcolor: alpha(hex, 0.08) } : {},
      }}
    >
      {loading ? (
        <Skeleton variant="text" width={24} sx={{ mx: 'auto' }} />
      ) : (
        <MDTypography
          variant="button"
          fontWeight={bold ? 'bold' : 'regular'}
          sx={{ color: count > 0 ? hex : '#b0b6c3' }}
        >
          {count}
        </MDTypography>
      )}
    </TableCell>
  );
}

export default function ProcedureStats() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('current_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [total, setTotal] = useState(0);
  const [byType, setByType] = useState(EMPTY_BY_TYPE);
  const [byStatus, setByStatus] = useState(EMPTY_BY_STATUS);
  const [matrix, setMatrix] = useState(EMPTY_MATRIX);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const range = resolveDateRange(preset, { from: customFrom, to: customTo });

  const fetchStats = useCallback(async () => {
    if (!range.from || !range.to) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.ivf.getProcedureDashboardStats({ date_from: range.from, date_to: range.to });
      setTotal(res?.data?.total || 0);
      setByType({ ...EMPTY_BY_TYPE, ...(res?.data?.by_type || {}) });
      setByStatus({ ...EMPTY_BY_STATUS, ...(res?.data?.by_status || {}) });
      const nextMatrix = { ...EMPTY_MATRIX };
      Object.entries(res?.data?.matrix || {}).forEach(([type, statuses]) => {
        nextMatrix[type] = { ...EMPTY_BY_STATUS, ...statuses };
      });
      setMatrix(nextMatrix);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load procedure stats');
      setTotal(0);
      setByType(EMPTY_BY_TYPE);
      setByStatus(EMPTY_BY_STATUS);
      setMatrix(EMPTY_MATRIX);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const goToProcedures = (type, status) => {
    if (!range.from || !range.to) return;
    const params = new URLSearchParams({
      proc_date_from: range.from,
      proc_date_to: range.to,
      // Explicit empty string so the list page's "default to CREATED" logic
      // (urlParams.has('proc_status') ? get() : 'CREATED') is bypassed —
      // "" means "all statuses", matching what these tiles/cells count.
      proc_status: status || '',
    });
    if (type) params.set('proc_type', type);
    navigate(`/ivf?${params.toString()}`);
  };

  return (
    <Card sx={{ p: 2.5, height: '100%' }}>
      <MDBox display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} mb={2.5}>
        <MDTypography variant="h6" fontWeight="bold">Procedures</MDTypography>
        <MDBox display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Date Range</InputLabel>
            <Select label="Date Range" value={preset} onChange={(e) => setPreset(e.target.value)}>
              {DATE_RANGE_PRESETS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {preset === 'custom' && (
            <>
              <MDDatePicker label="From" value={customFrom} onChange={(v) => setCustomFrom(v || '')} maxDate={customTo || undefined} />
              <MDDatePicker label="To" value={customTo} onChange={(v) => setCustomTo(v || '')} minDate={customFrom || undefined} />
            </>
          )}
        </MDBox>
      </MDBox>

      {error && (
        <MDTypography variant="caption" color="error" sx={{ display: 'block', mb: 1.5 }}>
          {error}
        </MDTypography>
      )}

      {/* Type-wise summary tiles */}
      <MDBox
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
          gap: 1.25,
          mb: 3,
        }}
      >
        <StatTile
          label="Total"
          count={total}
          hex={TOTAL_HEX}
          Icon={ScienceIcon}
          loading={loading}
          onClick={() => goToProcedures(null, null)}
          solid
        />
        {PROCEDURE_TYPE_LIST.map((type) => (
          <StatTile
            key={type}
            label={PROCEDURE_TYPE_LABELS[type]}
            count={byType[type] || 0}
            hex={PROCEDURE_TYPE_HEX[type]}
            Icon={BiotechIcon}
            loading={loading}
            onClick={() => goToProcedures(type, null)}
          />
        ))}
      </MDBox>

      {/* Detailed type x status matrix */}
      <MDTypography
        variant="caption"
        fontWeight="bold"
        color="text"
        sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}
      >
        Detailed Breakdown — Type × Status
      </MDTypography>
      <MDBox sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ borderBottom: '2px solid rgba(0,0,0,0.1)', py: 1 }}>
                <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Type
                </MDTypography>
              </TableCell>
              {PROCEDURE_STATUS_LIST.map((status) => (
                <TableCell key={status} align="center" sx={{ borderBottom: '2px solid rgba(0,0,0,0.1)', py: 1 }}>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ color: PROCEDURE_STATUS_HEX[status], textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}
                  >
                    {PROCEDURE_STATUS_LABELS[status]}
                  </MDTypography>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ borderBottom: '2px solid rgba(0,0,0,0.1)', py: 1 }}>
                <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Total
                </MDTypography>
              </TableCell>
            </TableRow>

            {PROCEDURE_TYPE_LIST.map((type) => (
              <TableRow
                key={type}
                sx={{ '&:hover': { bgcolor: alpha(PROCEDURE_TYPE_HEX[type], 0.04) } }}
              >
                <TableCell
                  onClick={() => goToProcedures(type, null)}
                  sx={{ cursor: 'pointer', py: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                >
                  <MDBox display="flex" alignItems="center" gap={0.75}>
                    <MDBox width={9} height={9} borderRadius="50%" flexShrink={0} sx={{ bgcolor: PROCEDURE_TYPE_HEX[type] }} />
                    <MDTypography variant="button" fontWeight="medium">
                      {PROCEDURE_TYPE_LABELS[type]}
                    </MDTypography>
                  </MDBox>
                </TableCell>
                {PROCEDURE_STATUS_LIST.map((status) => (
                  <Cell
                    key={status}
                    count={matrix[type]?.[status] || 0}
                    hex={PROCEDURE_STATUS_HEX[status]}
                    loading={loading}
                    onClick={() => goToProcedures(type, status)}
                  />
                ))}
                <Cell
                  count={byType[type] || 0}
                  hex={PROCEDURE_TYPE_HEX[type]}
                  loading={loading}
                  onClick={() => goToProcedures(type, null)}
                  bold
                />
              </TableRow>
            ))}

            <TableRow>
              <TableCell sx={{ py: 1, borderTop: '2px solid rgba(0,0,0,0.1)', borderBottom: 'none' }}>
                <MDTypography variant="button" fontWeight="bold">Total</MDTypography>
              </TableCell>
              {PROCEDURE_STATUS_LIST.map((status) => (
                <Cell
                  key={status}
                  count={byStatus[status] || 0}
                  hex={PROCEDURE_STATUS_HEX[status]}
                  loading={loading}
                  onClick={() => goToProcedures(null, status)}
                  bold
                />
              ))}
              <TableCell align="center" sx={{ py: 1, borderTop: '2px solid rgba(0,0,0,0.1)', borderBottom: 'none' }}>
                {loading ? (
                  <Skeleton variant="text" width={28} sx={{ mx: 'auto' }} />
                ) : (
                  <MDTypography variant="button" fontWeight="bold" sx={{ color: TOTAL_HEX }}>
                    {total}
                  </MDTypography>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </MDBox>

      <MDTypography variant="caption" color="text" sx={{ display: 'block', mt: 1.5 }}>
        Click any card or cell to view the filtered procedure list →
      </MDTypography>
    </Card>
  );
}
